import React, { useState, useEffect, useContext } from "react";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import BASEURL from "../Utils/Baseurl";
import axios from "axios";
import {
  Card,
  Stack,
  CardHeader,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import MaterialTypography from "../../components/MaterialTypography";
import MaterialButton from "../../components/MaterialButton";
import MaterialDataGrid from "../../components/MaterialDataGrid";
import MaterialInput from "../../components/MaterialInput";
import MaterialSelect from "../../components/MasterSelect";
import UserDetailsContext from "../../context/auth";
import Swal from "sweetalert2";
import moment from "moment";
import useBranchAccess from "../../hooks/useBranchAccess";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const missingPunchRecordsQuery = `
query MissingPunchRecords($month: Int!, $year: Int!, $branch: [String], $employeeType: [String]) {
  missingPunchRecords(month: $month, year: $year, branch: $branch, employeeType: $employeeType) {
    id
    user {
      id
      empid
      firstName
      lastName
      branch
      employeeType
    }
    checkIn
    checkOut
    totalWorkingHours
    overtime
    shiftName
    isAutoClosed
    isMissingCheckin
    isLate
    lateMinutes
  }
}`;

const createRegularizationMutation = `
mutation RegularizeAttendance($input: CreateRegularizationInput!) {
  regularizationApprovalHR(input: $input) {
    regularizationApproval {
      id
      status
    }
  }
}`;

const branchOptionsQuery = `
query {
  allBranch {
    id
    name
  }
}`;

const MissingPunches = () => {
  const { user } = useContext(UserDetailsContext);
  const { isBranchAdmin, userBranch } = useBranchAccess();
  const now = new Date();

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Regularize dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [regCheckIn, setRegCheckIn] = useState("");
  const [regCheckOut, setRegCheckOut] = useState("");
  const [regReason, setRegReason] = useState("");

  useEffect(() => {
    if (isBranchAdmin && userBranch) {
      setSelectedBranch({ value: userBranch, label: userBranch });
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    // Don't fetch until branch is set for branch admins
    if (isBranchAdmin && !selectedBranch) return;
    fetchRecords();
  }, [month, year, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const res = await axios.post(
        BASEURL,
        { query: branchOptionsQuery },
        { headers: { "Content-Type": "application/json" } }
      );
      const branches = res.data.data.allBranch || [];
      setBranchOptions(
        branches.map((b) => ({ value: b.name, label: b.name }))
      );
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const variables = {
        month,
        year,
        branch: selectedBranch ? [selectedBranch.value] : null,
      };
      const res = await axios.post(
        BASEURL,
        { query: missingPunchRecordsQuery, variables },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = res.data.data.missingPunchRecords || [];
      setRecords(data);
    } catch (err) {
      console.error("Error fetching missing punch records:", err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "empid",
      headerName: "Emp ID",
      flex: 0.7,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "employeeName",
      headerName: "Employee",
      flex: 1.2,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "branch",
      headerName: "Branch",
      flex: 0.8,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 0.8,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "checkIn",
      headerName: "Check In",
      flex: 1,
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params) =>
        params.row.isMissingCheckin ? (
          <Chip label="Missing" color="error" size="small" variant="outlined" />
        ) : (
          params.value || "—"
        ),
    },
    {
      field: "checkOut",
      headerName: "Check Out",
      flex: 1,
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params) =>
        params.row.isAutoClosed ? (
          <Chip
            label={`${params.value} (Auto)`}
            color="warning"
            size="small"
            variant="outlined"
          />
        ) : (
          params.value || "—"
        ),
    },
    {
      field: "shiftName",
      headerName: "Shift",
      flex: 0.8,
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "issueType",
      headerName: "Issue",
      flex: 1,
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "Missing Checkout" ? "warning" : "error"}
          size="small"
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => (
        <MaterialButton
          variant="text"
          color="dark"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openRegularizeDialog(params.row);
          }}
        >
          Regularize
        </MaterialButton>
      ),
    },
  ];

  const rows = records.map((r) => {
    const checkInDate = r.checkIn
      ? moment(r.checkIn).format("DD-MM-YYYY")
      : r.checkOut
      ? moment(r.checkOut).format("DD-MM-YYYY")
      : "—";

    return {
      id: r.id,
      empid: r.user?.empid || "—",
      employeeName: `${r.user?.firstName || ""} ${r.user?.lastName || ""}`.trim(),
      branch: r.user?.branch || "—",
      date: checkInDate,
      checkIn: r.checkIn ? moment(r.checkIn).format("hh:mm A") : null,
      checkOut: r.checkOut ? moment(r.checkOut).format("hh:mm A") : null,
      shiftName: r.shiftName,
      isAutoClosed: r.isAutoClosed,
      isMissingCheckin: r.isMissingCheckin,
      issueType: r.isAutoClosed
        ? "Missing Checkout"
        : r.isMissingCheckin
        ? "Missing Checkin"
        : (!r.checkOut && r.checkIn)
        ? "Missing Checkout"
        : "Unknown",
      userId: r.user?.id,
      rawCheckIn: r.checkIn,
      rawCheckOut: r.checkOut,
    };
  });

  const openRegularizeDialog = (row) => {
    setSelectedRecord(row);
    // Pre-fill with existing values
    if (row.rawCheckIn) {
      setRegCheckIn(moment(row.rawCheckIn).format("YYYY-MM-DDTHH:mm"));
    } else {
      setRegCheckIn("");
    }
    if (row.rawCheckOut) {
      setRegCheckOut(moment(row.rawCheckOut).format("YYYY-MM-DDTHH:mm"));
    } else {
      setRegCheckOut("");
    }
    setRegReason("");
    setDialogOpen(true);
  };

  const handleRegularize = async () => {
    if (!regCheckIn || !regCheckOut) {
      Swal.fire("Error", "Both Check In and Check Out are required.", "error");
      return;
    }
    if (!regReason.trim()) {
      Swal.fire("Error", "Reason is required.", "error");
      return;
    }

    // Validate dates before sending
    const checkInDate = new Date(regCheckIn);
    const checkOutDate = new Date(regCheckOut);
    if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
      Swal.fire("Error", "Invalid date/time entered. Please check the values.", "error");
      return;
    }
    if (checkOutDate <= checkInDate) {
      Swal.fire("Error", "Check Out must be after Check In.", "error");
      return;
    }

    try {
      const variables = {
        input: {
          userId: selectedRecord.userId,
          checkIn: checkInDate.toISOString(),
          checkOut: checkOutDate.toISOString(),
          reason: regReason,
          autoApprove: true,
        },
      };

      const res = await axios.post(
        BASEURL,
        { query: createRegularizationMutation, variables },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data.errors) {
        const errMsg = res.data.errors[0]?.message || "Unknown error";
        Swal.fire("Error", errMsg, "error");
        return;
      }
      const result = res.data.data?.regularizationApprovalHR;
      if (result?.regularizationApproval) {
        Swal.fire("Success", "Attendance regularized successfully.", "success");
        setDialogOpen(false);
        fetchRecords();
      } else {
        Swal.fire("Error", "Failed to regularize attendance.", "error");
      }
    } catch (err) {
      console.error("Error regularizing:", err);
      const errMsg = err?.response?.data?.errors?.[0]?.message || "Failed to regularize attendance. Please try again.";
      Swal.fire("Error", errMsg, "error");
    }
  };

  const yearOptions = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) {
    yearOptions.push({ value: y, label: String(y) });
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Missing Punches
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                Auto-closed checkouts and missing checkins for review
              </MaterialTypography>
            </Stack>
          }
        />

        {/* Filters */}
        <Stack direction="row" spacing={2} px={3} pb={2} alignItems="center">
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 6, md: 2 }}>
              <MaterialSelect
                label="Month"
                options={MONTH_OPTIONS}
                value={MONTH_OPTIONS.find((o) => o.value === month)}
                onChange={(selected) => setMonth(selected?.value || now.getMonth() + 1)}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <MaterialSelect
                label="Year"
                options={yearOptions}
                value={yearOptions.find((o) => o.value === year)}
                onChange={(selected) => setYear(selected?.value || now.getFullYear())}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <MaterialSelect
                label="Branch"
                options={[{ value: null, label: "All Branches" }, ...branchOptions]}
                value={selectedBranch || { value: null, label: "All Branches" }}
                onChange={(selected) =>
                  setSelectedBranch(selected?.value ? selected : null)
                }
                isClearable
                isDisabled={isBranchAdmin}
              />
            </Grid>
          </Grid>
        </Stack>

        <MaterialDataGrid
          rows={rows}
          columns={columns}
          checkboxSelection={false}
          isSelectable={false}
          loading={loading}
          localeText={{
            noRowsLabel: (
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                <MaterialTypography variant="body2" fontWeight="medium" color="text">
                  No missing punch records found
                </MaterialTypography>
                <MaterialTypography variant="caption" color="text">
                  All attendance records for this period have valid check-in and check-out.
                </MaterialTypography>
              </Stack>
            ),
          }}
        />
      </Card>

      {/* Regularize Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <MaterialTypography variant="body1" fontWeight="medium">
            Regularize Attendance — {selectedRecord?.employeeName} ({selectedRecord?.date})
          </MaterialTypography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 12, md: 6 }}>
              <MaterialInput
                label="Check In"
                type="datetime-local"
                value={regCheckIn}
                onChange={(e) => setRegCheckIn(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MaterialInput
                label="Check Out"
                type="datetime-local"
                value={regCheckOut}
                onChange={(e) => setRegCheckOut(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MaterialInput
                label="Reason"
                value={regReason}
                onChange={(e) => setRegReason(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MaterialButton
            variant="text"
            color="error"
            onClick={() => setDialogOpen(false)}
          >
            Cancel
          </MaterialButton>
          <MaterialButton
            variant="text"
            color="dark"
            onClick={handleRegularize}
          >
            Regularize
          </MaterialButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default MissingPunches;
